import { utc } from '@date-fns/utc';
import { zodResolver } from '@hookform/resolvers/zod';
import { parse } from 'date-fns';
import { DeepPartial, FieldErrors, Resolver, ResolverOptions } from 'react-hook-form';
import z from 'zod';

import {
  ApiUnlimitedCloudHeightZod,
  ApiVonaCloudZod,
  ApiVonaVolcanoZod,
  ApiVonaZod,
  SummitHeightZod,
  UNKNOWN_VOLCANO_NAME,
  UNKNOWN_VOLCANO_NUMBER,
} from '@vona/shared';
import { ActivityStatusText, ActivityZod } from '@vona/shared/Activity';
import { DurationTypeText, SimpleDurationTypes, ValueDurationTypes } from '@vona/shared/Duration';
import { HeightUnitZod, HeightZod } from '@vona/shared/Height';
import { RequiredMessage } from '@vona/shared/util/RequiredMessage';

const FormDatetimeZod = z.string().regex(/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/, {
  message: 'Date / time must be in the format DD/MM/YYYY HH:MM',
}).transform((val, ctx) => {
  try {
    const parsed = parse(val, 'dd/MM/yyyy HH:mm', new Date(), { in: utc });
    return parsed.toISOString();
  } catch {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Invalid Date',
    });
    return z.NEVER;
  }
});

const FormDurationZod = z.discriminatedUnion('type', [z.object({
  type: z.enum(SimpleDurationTypes),
}), z.object({
  type: z.literal('VALUE'),
  value: z.string().regex(/^\d{1,3}$/, { message: 'Duration must be an integer number between 0 and 999' }),
  unit: z.enum(ValueDurationTypes, RequiredMessage),
})]);

const FormOnsetTimeZod = z.discriminatedUnion('type', [z.object({
  type: z.enum(['NIL', 'UNKNOWN']),
}), z.object({
  type: z.literal('KNOWN'),
  time: FormDatetimeZod,
})]);

const FormCloudZod = z.discriminatedUnion('state', [
  z.object({ state: z.literal('NO') }),
  ApiVonaCloudZod.extend({ state: z.literal('YES_HEIGHT_UNKNOWN') }).omit({ height: true }),
  ApiVonaCloudZod.extend({
    state: z.literal('YES_HEIGHT_KNOWN'),
    height: ApiUnlimitedCloudHeightZod.extend({
      value: z.string().regex(/^[-]?\d+$/, { message: 'Height must be an integer number' }),
    }).transform((val) => ({
      ...val,
      value: Number(val.value),
    })).pipe(ApiVonaCloudZod.shape.height).transform((val) => (val ? {
      ...val,
      value: String(val.value),
    } : null)),
  }),
]);

const FormSummitHeightZod = HeightZod.extend({
  unit: HeightUnitZod.optional(),
  value: z.string().regex(/^(?:UNKNOWN|[-]?\d+)$/i, { message: 'Height must be an integer number or "UNKNOWN"' }),
}).superRefine((val, ctx) => {
  if (val.value.toUpperCase() !== 'UNKNOWN') {
    const parseResult = SummitHeightZod.safeParse({ ...val, value: Number(val.value) });
    if (!parseResult.success) {
      parseResult.error.errors.forEach((error) => {
        ctx.addIssue(error);
      });
    }
  }
});

const WillUpdateZod = z.discriminatedUnion('willUpdate', [
  z.object({ willUpdate: z.literal(false), nextNotice: z.string().optional() }),
  z.object({ willUpdate: z.literal(true), nextNotice: FormDatetimeZod }),
]);

/**
 * Heavily based on (i.e. extended from) the ApiVonaZod schema, but with some
 * fields omitted and some fields transformed to be usable by react hook form.
 */
export const FormVonaZod = ApiVonaZod.omit({
  issuedAt: true,
  activity: true,
  onsetTime: true,
  nextNotice: true,
}).extend({
  activity: z.object({ status: z.string() }).passthrough().pipe(ActivityZod),
  volcano: ApiVonaVolcanoZod.extend({
    summitHeightAmsl: FormSummitHeightZod,
  }),
  onset: FormOnsetTimeZod,
  duration: FormDurationZod,
  cloud: FormCloudZod,
}).and(WillUpdateZod);
export type RawFormVona = z.infer<typeof FormVonaZod>;
export type FormVona = DeepPartial<RawFormVona>;

export const validateVona: Resolver<FormVona, unknown> = async (values, context, options) => {
  const result = await zodResolver(FormVonaZod, {
    errorMap: (issue, ctx) => {
      if (issue.code === 'invalid_union_discriminator') {
        // This error only shows up (for now) when a selector is unselected, so
        // we'll override the message to make it more user-friendly. Using the
        // RequiredMessage constant here allows the UI to just highlight the input
        // without showing an actual error message.
        return RequiredMessage;
      }
      return { message: ctx.defaultError };
    },
  })(
    values as RawFormVona,
    context,
    options as ResolverOptions<RawFormVona>,
  );
  const zodSuccess = !Object.keys(result.errors).length;
  const errors: FieldErrors<FormVona> = zodSuccess ? {} : result.errors;

  if (values.volcano?.name === UNKNOWN_VOLCANO_NAME) {
    if (values.volcano?.number !== UNKNOWN_VOLCANO_NUMBER) {
      errors.volcano = errors.volcano || {};
      errors.volcano.number = {
        type: 'validate',
        message: `Volcano number must be "${UNKNOWN_VOLCANO_NUMBER}" when volcano is Unknown \
(select Unknown from the dropdown)`,
      };
    }
  } else if (values.volcano?.number === UNKNOWN_VOLCANO_NUMBER) {
    errors.volcano = errors.volcano || {};
    errors.volcano.number = {
      type: 'validate',
      message: `Volcano number "${UNKNOWN_VOLCANO_NUMBER}" is only valid for an Unknown volcano`,
    };
  }

  const status = values.activity?.status;
  if (status === 'NIL' && values.onset?.type !== 'NIL') {
    errors.onset = errors.onset || {};
    errors.onset.type = {
      type: 'validate',
      message: `Onset must be "Nil" when activity status is "${ActivityStatusText.NIL}"`,
    };
  }

  const durationType = values.duration?.type;
  const durationOngoing = values.duration?.type === 'ONGOING_CONS' || values.duration?.type === 'ONGOING_EPISODIC';
  if (status === 'NIL' && durationType !== 'NIL') {
    errors.duration = errors.duration || {};
    errors.duration.type = {
      type: 'validate',
      message: `Duration must be "${DurationTypeText.NIL}" when activity status is "${ActivityStatusText.NIL}"`,
    };
  } else if (status === 'ERUPTION_ONGOING' && !durationOngoing) {
    errors.duration = errors.duration || {};
    errors.duration.type = {
      type: 'validate',
      message: `Duration must be "${DurationTypeText.ONGOING_CONS}" or "${DurationTypeText.ONGOING_EPISODIC}"
        when activity status is "${ActivityStatusText.ERUPTION_ONGOING}"`,
    };
  } else if (status !== 'ERUPTION_ONGOING' && durationOngoing) {
    errors.duration = errors.duration || {};
    errors.duration.type = {
      type: 'validate',
      message: `Duration must not be "${DurationTypeText.ONGOING_CONS}" or "${DurationTypeText.ONGOING_EPISODIC}"
        unless activity status is "${ActivityStatusText.ERUPTION_ONGOING}"`,
    };
  }

  if (values.willUpdate && !values.nextNotice) {
    errors.nextNotice = {
      type: 'validate',
      message: 'Next notice time must be set when "Will update" is checked',
    };
  }

  return { errors, values: (!Object.keys(errors).length ? values : {}) };
};
